import React, {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  LoaderSpinner,
  Modal,
  ModalHeader,
  SelectedTabProvider,
  Tab,
  TabContent,
  Tabs,
} from '@lightspeed/design-system-react'
import { vn } from 'vend-number'
import { ApiTransaction } from '@vend/types'
import {
  PAYMENT_TYPES,
  useFeatures,
  usePMOFFeature,
} from '@vend/business-components'
import { removeFromStorage, trackError, trackUserEvent } from '@vend/utilities'
import uuid from '@vend/vend-uuid'
import { useT } from '@transifex/react'
import { SwiperError } from '../helpers/lspay.types'
import {
  getUnrefRefundReason,
  shouldAllowUnreferencedRefunds,
} from '../helpers/refund'
import { useTrackTransactionDuration } from '../hooks/useTrackTransactionDuration'
import {
  useUserManager,
  useVdCurrencyFilter,
} from '../../../../ng-compat/hooks'
import { getLspayErrorMessage } from '../helpers/errorChecking'
import { useFetchPairing } from '../hooks/useFetchPairing'
import { ACTIVE_LSPAY_TRANSACTION } from '../../../../constants/payments'
import {
  LSPAY_ERROR,
  PAYMENT_MODAL_TAB,
  PAYMENT_MODAL_TAB_DATA_TRACK,
} from '../helpers/lspayConstants'
import { WEB_REGISTER } from '../../../web-register.constant'
import { useErrorMessages } from '../hooks/useErrorMessages'
import { useShouldUseSimulator } from '../hooks/useShouldUseSimulator'
import { selectSaleCustomer } from '../../../../state/slices/currentSale/currentSaleSlice'
import { isValidCustomer } from '../../../../functions/sale'
import { PERMISSIONS } from '../../../../constants'
import { selectTrainingModeEnabled } from '../../../../state/slices/posState/posStateSlice'
import { isAblyCommunicationSupported } from '../../../realtime-provider/ably-version-check'
import { useTerminalStatusFromRedux } from '../../terminal-status/useTerminalStatusFromRedux'
import { useAppDispatch, useAppSelector } from '../../../../state/hooks'
import { SsboTourStep } from '../../../sale/ssbo-guided-tour/SsboTourStep'
import { TerminalStatusContent } from '../../terminal-status/TerminalStatusContent'
import { setAttemptPayment } from '../../../../state/slices/terminalStatus/terminalStatusSlice'
import { useLspayCnpOnboardingSignpost } from '../hooks/useLspayCnpOnboardingSignpost'
import { LSPAYPaymentModalProps } from './LSPAYPaymentModal.types'
import { LSPAYCnpOnlyPaymentModal } from './LSPAYCnpOnlyPaymentModal'
import { LSPAYRefundClientDriven } from './refund/LSPAYRefundClientDriven'
import { LSPAYReferencedRefundOnly } from './refund/LSPAYReferencedRefundOnly'
import { LSPAYRefundServerDriven } from './refund/server-driven/LSPAYRefundServerDriven'
import { LSPAYPaymentTerminal } from './payment/LSPAYPaymentTerminal'
import { LSPAYCardNotPresentPayment } from './payment/LSPAYCardNotPresentPayment'
import { LSPAYCnpPmofPayment } from './payment/LSPAYCnpPmofPayment'
import { ErrorContent } from './modal-states/ErrorContent'
import { LSPAYPaymentServerDriven } from './payment/lspay-payment-server-driven/LSPAYPaymentServerDriven'
import { LSPAYPaymentMethodOnFile } from './payment/LSPAYPaymentMethodOnFile'
import { LSPAYCnpOnboardingSignpost } from './modal-states/LSPAYCnpOnboardingSignpost'

export const LSPAYPaymentModal: FC<LSPAYPaymentModalProps> = props => {
  const amount = useMemo(() => vn(props.amount), [props.amount])

  if (props.forceFlow === 'cnp' && !amount.isNegative()) {
    return <LSPAYCnpOnlyPaymentModal {...props} />
  }

  return <LSPAYPaymentModalContent {...props} />
}

const LSPAYPaymentModalContent: FC<LSPAYPaymentModalProps> = ({
  amount: rawAmount,
  outletId,
  currency,
  register,
  registerSaleId,
  saleTotal,
  originalRegisterSalePaymentIds,
  originalSalePaidByOtherPT,
  originalSaleHasProcessingServicePayment,
  originalLspayPaymentAmount,
  retailerPaymentType,
  transactionId: initialTransactionId,
  paymentIntentId,
  isRecovery,
  withSurcharge,
  tipEligibleAmount: rawTipEligibleAmount,
  close: originalClose,
  forceFlow,
  paymentMethodOnFileId,
}) => {
  const dispatch = useAppDispatch()
  const [transactionId, setTransactionId] = useState<string>(
    initialTransactionId
  )
  const [previousSelectedTab, setPreviousSelectedTab] = useState<string>(
    PAYMENT_MODAL_TAB.TERMINAL
  )
  const [features, featuresLoading] = useFeatures(
    [
      'lspay_unreferenced_refund',
      'lspay_international_unreferenced_refunds',
      'lspay_cnp_payments',
      'lspay_cnp_moto',
      'lspay_retrieve_pi_on_error',
      'lspay_refund_with_server_driven',
      'lspay_referenced_refund_with_server_driven',
      'lspay_terminal_status_enabled',
      'lspay_terminal_status_connection_logging_enabled',
      'enable_customer_saved_cards',
      'lspay_cp_cnp_split_entry',
    ],
    {
      lspay_unreferenced_refund: false,
      lspay_international_unreferenced_refunds: false,
      lspay_cnp_payments: false,
      lspay_cnp_moto: false,
      lspay_retrieve_pi_on_error: true,
      lspay_refund_with_server_driven: false,
      lspay_referenced_refund_with_server_driven: false,
      lspay_terminal_status_enabled: false,
      lspay_terminal_status_connection_logging_enabled: false,
      enable_customer_saved_cards: false,
      lspay_cp_cnp_split_entry: false,
    }
  )

  const {
    loading: pairingLoading,
    pairing,
    error: pairingError,
    refetch: refetchPairing,
    isServerDrivenSupported: payWithServerDriven,
  } = useFetchPairing(register.id)

  const shouldUseSimulator = useShouldUseSimulator()
  const shouldUseServerDrivenPayment =
    !!isRecovery || (payWithServerDriven && !shouldUseSimulator)
  const vdCurrencyFilter = useVdCurrencyFilter()
  const [dismissible, setDismissible] = useState<boolean>(false)
  const [disableCNP, setDisableCNP] = useState<boolean>(false)
  const [disableTerminalPayment, setDisableTerminalPayment] = useState<boolean>(
    false
  )
  const [intentClientSecret, setIntentClientSecret] = useState<string>()
  const [swiperError, setSwiperError] = useState<SwiperError>()
  // Use a ref to track cancelProcessing synchronously, avoiding race conditions
  // when the user switches tabs before the createTransaction API completes.
  // Type allows both direct cancel functions and wrappers that return cancel functions.
  const cancelProcessingRef = useRef<(() => void | (() => void)) | undefined>(
    undefined
  )
  const [cancelProcessingState, setCancelProcessingState] = useState<
    (() => void | (() => void)) | undefined
  >(undefined)
  // Track whether cancel has already been invoked to prevent double-calling
  const cancelInvokedRef = useRef<boolean>(false)
  // Track whether the payment flow has started to prevent PaymentComponent from
  // being unmounted and remounted with the same transactionId when terminal
  // status changes arrive after the transaction has already been created.
  const hasPaymentStartedRef = useRef<boolean>(false)

  // Wrapper function to keep both ref and state in sync.
  // Child components may pass the cancel function wrapped (e.g. () => cancelProcessing)
  // to avoid React treating it as a state updater, so we store it as-is and normalize
  // when invoking.
  const setCancelProcessing = useCallback(
    (fn: (() => void | (() => void)) | undefined) => {
      cancelProcessingRef.current = fn
      setCancelProcessingState(fn)
      // Reset the invoked flag when a new cancel function is set
      cancelInvokedRef.current = false
    },
    []
  )

  // Helper to invoke cancel, handling both direct functions and wrappers.
  // This is idempotent - it will only execute the cancel once even if called multiple times.
  const invokeCancel = useCallback(() => {
    if (!cancelProcessingRef.current || cancelInvokedRef.current) return

    // Mark as invoked before calling to prevent re-entrance
    cancelInvokedRef.current = true

    const result = cancelProcessingRef.current()
    // If the result is a function (wrapped), call it; otherwise it was already invoked
    if (typeof result === 'function') {
      result()
    }
  }, [])

  // Guarded version of cancel to pass to child components
  // This ensures children can't bypass the idempotency guard
  const guardedCancelProcessing = useMemo(
    () => (cancelProcessingState ? invokeCancel : undefined),
    [cancelProcessingState, invokeCancel]
  )
  const amount = useMemo(() => vn(rawAmount), [rawAmount])
  const tipEligibleAmount = useMemo(() => vn(rawTipEligibleAmount), [
    rawTipEligibleAmount,
  ])
  const isRefund = amount.isNegative()

  const trackEnd = useTrackTransactionDuration({
    registerId: register.id,
    transactionId: initialTransactionId,
    paymentTypeId: PAYMENT_TYPES.LSPAY,
    isRefund,
    amount: amount.toNumber(),
  })

  const saleCustomer = useAppSelector(selectSaleCustomer)
  const customerId = isValidCustomer(saleCustomer) ? saleCustomer.id : undefined

  const getErrorMessage = useErrorMessages()
  const t = useT()

  const closeModal = useCallback(
    (val?: Promise<ApiTransaction>) => {
      const completed = !!val
      trackEnd(completed)

      // Once the modal is closing (terminal state for this component),
      // clear the cancel callback to avoid any late or duplicate cancellations.
      cancelProcessingRef.current = undefined
      hasPaymentStartedRef.current = false

      originalClose(val)

      // Reset attempt payment state in terminal status slice
      dispatch(setAttemptPayment(false))
    },
    [originalClose, trackEnd, dispatch]
  )

  // Separate close handler for user-initiated cancellations (cancel button,
  // error dismissal, etc.). This sends a cancel request to the terminal to
  // ensure any in-flight transaction is cleaned up, then delegates to closeModal.
  const cancelAndCloseModal = useCallback(() => {
    invokeCancel()
    closeModal()
  }, [invokeCancel, closeModal])
  const generateTransactionId = useCallback(() => {
    const transactionId = uuid()
    setTransactionId(transactionId)
    return transactionId
  }, [])

  const {
    isLoading: isRequestingTerminalStatus,
    warningType,
    isAttemptingPayment,
  } = useTerminalStatusFromRedux()

  // Terminal status is only applicable when the feature flag is on AND the
  // paired terminal meets the minimum app version for Ably communication.
  // Without the version check the modal would wait forever for status updates
  // that the realtime-event-handler never dispatches (it gates on the same
  // version requirement before mounting TerminalStatusEventHandler).
  const isTerminalStatusEnabled =
    features.lspay_terminal_status_enabled &&
    !!pairing &&
    isAblyCommunicationSupported(pairing.appVersion)

  // When terminal status is enabled, `isRequestingTerminalStatus` can be null/undefined
  // (meaning the status is not yet known), which should still be treated as loading.
  // Using `!== false` ensures null/undefined is considered loading, not just explicit `true`.
  // When the feature is disabled (or unsupported) we fall back to a plain truthy check
  // since the terminal status state is never populated and will remain falsy.
  const isTerminalStatusLoading = isTerminalStatusEnabled
    ? isRequestingTerminalStatus !== false
    : isRequestingTerminalStatus

  // Once the payment flow has started (hasPaymentStartedRef is true), terminal
  // status loading changes must not flip isLoading back to true. Doing so would
  // unmount PaymentComponent and cause useCreateTransaction to re-fire with the
  // same registerSalePaymentId, producing a duplicate transaction error.
  const isLoading =
    featuresLoading ||
    pairingLoading ||
    (!hasPaymentStartedRef.current && isTerminalStatusLoading)

  // Compute this before the early returns so the useLayoutEffect below can be
  // called unconditionally (hooks must not be called after a conditional return).
  const showTerminalStatusWarning =
    isTerminalStatusEnabled &&
    warningType &&
    !isRequestingTerminalStatus &&
    !isAttemptingPayment &&
    !hasPaymentStartedRef.current

  // Once the PaymentComponent is committed to the DOM, lock the flag so that
  // late-arriving terminal status changes cannot unmount/remount it with the
  // same transactionId. useLayoutEffect runs synchronously after DOM commit,
  // before the browser yields to external events like Ably WebSocket messages,
  // eliminating any window where a Redux update could re-render with the ref
  // still unset.
  useLayoutEffect(() => {
    if (
      !isLoading &&
      !swiperError &&
      !pairingError &&
      !showTerminalStatusWarning &&
      !isRefund
    ) {
      hasPaymentStartedRef.current = true
    }
  }, [
    isLoading,
    swiperError,
    pairingError,
    showTerminalStatusWarning,
    isRefund,
  ])

  // Payment method on file related variables
  const { isEnabled: lspayPMOFSettingEnabled } = usePMOFFeature()
  const lspayPMOFuserPermissionGranted = useUserManager().hasPermission(
    PERMISSIONS.LSPAY_PMOF_ADD_EDIT_USE
  )
  const trainingMode = useAppSelector(selectTrainingModeEnabled)

  // Track the user event only if the payment is not initiated with server-driven.
  // Wrapped in useEffect to avoid firing as a side effect during render.
  useEffect(() => {
    if (!isLoading && !shouldUseServerDrivenPayment) {
      trackUserEvent(
        WEB_REGISTER.DISPLAY_NAME,
        'LSPAY Client Driven flow should start',
        {
          payWithServerDriven,
          pairing,
          pairingLoading,
        }
      )
    }
  }, [
    isLoading,
    shouldUseServerDrivenPayment,
    payWithServerDriven,
    pairing,
    pairingLoading,
  ])

  const onError = useCallback(() => {
    if (shouldUseServerDrivenPayment) {
      // If the payment was initiated with server-driven, we need to remove the active transaction from storage
      removeFromStorage(ACTIVE_LSPAY_TRANSACTION)
    }
    // close the popup with no argument - it is picked up as a failure
    cancelAndCloseModal()
  }, [cancelAndCloseModal, shouldUseServerDrivenPayment])

  // NOTE: lspay_cp_cnp_split_entry=true is not production-supported until gated rollout and QA are complete.
  const { shouldShowSignpost, dismissSignpost } = useLspayCnpOnboardingSignpost(
    {
      source: 'cpModal',
      // Disable the hook when the flag is off so it does not trigger a network
      // call or fire telemetry in production where the signpost never renders.
      // TODO: LSPAY-43773 — once fully rolled out and stable in Prod, remove
      // this gating; the signpost should always be enabled.
      enabled: features.lspay_cp_cnp_split_entry ?? false,
    }
  )

  const [signpostTabSelected, setSignpostTabSelected] = useState(false)

  const handleSignpostDismiss = useCallback(() => {
    // dismissSignpost tracks its own "Dismissal Failed" telemetry internally;
    // this catch only guards against unexpected errors so they're not lost.
    dismissSignpost().catch(err => trackError(err))
    cancelAndCloseModal()
  }, [dismissSignpost, cancelAndCloseModal])

  if (isLoading) {
    return (
      <Modal size="medium" focusTrapActive={true}>
        <div
          className="vd-flex vd-flex--justify-center"
          style={{ height: '100%' }}
        >
          <LoaderSpinner />
        </div>
      </Modal>
    )
  }

  if (swiperError || pairingError) {
    const lspayError = swiperError
      ? getLspayErrorMessage({
          err: swiperError,
          vdCurrencyFilter,
          retailerPaymentTypeName: retailerPaymentType.name,
          t,
          getErrorMessage,
        })
      : getErrorMessage(LSPAY_ERROR.DEFAULT_PAYMENT)
    return (
      <Modal size="medium" focusTrapActive={true} dismissable={true}>
        <ErrorContent
          disableRetry={!pairingError} // Retry only if there is a pairing error
          retry={pairingError ? () => refetchPairing() : () => {}}
          close={cancelAndCloseModal}
          lspayError={lspayError}
        />
      </Modal>
    )
  }

  const {
    lspay_unreferenced_refund: lspayUnreferencedRefundFlag,
    lspay_cnp_payments: lspayCNPPaymentsFlag,
    lspay_cnp_moto: lspayCNPMotoFlag,
    lspay_international_unreferenced_refunds: lspayInternationalUnreferencedRefundFeatureFlag,
    lspay_refund_with_server_driven: lspayRefundWithServerDriven,
    lspay_referenced_refund_with_server_driven: lspayReferencedRefundWithServerDriven,
    enable_customer_saved_cards: lspayPMOFFeatureFlag,
    lspay_cp_cnp_split_entry: lspayCpCnpSplitEntryFlag,
  } = features

  const isSettingEnabledByMerchantAndFlagEnabled =
    lspayPMOFFeatureFlag && lspayPMOFSettingEnabled

  const isCurrentUserSessionEligibleForPMOF =
    lspayPMOFuserPermissionGranted && !trainingMode

  const isCustomerSetOnSale = !!customerId

  const useProcessingPMOF =
    isSettingEnabledByMerchantAndFlagEnabled &&
    isCurrentUserSessionEligibleForPMOF &&
    isCustomerSetOnSale

  const allowUnrefRefund = shouldAllowUnreferencedRefunds(
    lspayUnreferencedRefundFlag,
    lspayInternationalUnreferencedRefundFeatureFlag,
    currency
  )

  // For server-driven refunds, we consider both referenced and unreferenced refunds.
  // The function `getUnrefRefundReason` will return a reason if it is an unreferenced refund.
  // We have developed two phases for refunds:
  // 1. lspayRefundWithServerDriven for unreferenced refunds.
  // 2. lspayReferencedRefundWithServerDriven for referenced refunds.
  // if both are enabled, all refunds will be server-driven.
  // However, PMOF payments must use the client-driven (legacy API endpoint) flow as the
  // server-driven API endpoint doesn't support Processing Service refunds yet.
  const shouldServerDrivenRefund =
    isRefund && // Refund check
    !originalSaleHasProcessingServicePayment && // Processing Service payments use the legacy API endpoint
    payWithServerDriven && // Server-driven check, which is set based on pairing
    lspayRefundWithServerDriven && // FF check
    (lspayReferencedRefundWithServerDriven || // FF check for referenced refund
      !!getUnrefRefundReason(
        // Unreferenced refund check
        originalRegisterSalePaymentIds,
        originalSalePaidByOtherPT
      ))

  const showHeader = !isRefund && lspayCNPPaymentsFlag

  const lspayRegister = {
    id: register.id,
    name: register.name,
  }

  const PaymentComponent = shouldUseServerDrivenPayment
    ? LSPAYPaymentServerDriven
    : LSPAYPaymentTerminal

  // For refunds, use the client-driven component (legacy API endpoint) if:
  // 1. Server-driven refund conditions are not met, OR
  // 2. Using simulator, OR
  // 3. The original payment was processed via Processing Service
  const RefundComponent =
    shouldServerDrivenRefund && !shouldUseSimulator
      ? LSPAYRefundServerDriven
      : LSPAYRefundClientDriven

  const handleOnChange = (selectedTab: string) => {
    // Signpost tab: do not cancel the terminal or change transaction state.
    if (selectedTab === PAYMENT_MODAL_TAB.SIGNPOST) {
      setSignpostTabSelected(true)
      // Make the modal dismissable while the signpost is visible so the X button
      // shows. Passive close (X) does not persist dismissal (only "Don't show again" does).
      setDismissible(true)
      // Do NOT update previousSelectedTab here — keeping it as 'terminal' ensures
      // that switching back from signpost to terminal is a no-op for transactionId
      // generation (previousSelectedTab === selectedTab, so the switch block is skipped).
      return
    }

    setSignpostTabSelected(false)
    // Restore the modal to its default non-dismissable state so the X button
    // matches what it was before the signpost tab was selected. PaymentComponent
    // will re-enable dismissal when it reaches an appropriate state.
    setDismissible(false)

    if (previousSelectedTab !== selectedTab && shouldUseServerDrivenPayment) {
      if (selectedTab === PAYMENT_MODAL_TAB.TERMINAL) {
        const oldRegisterSalePaymentId = transactionId

        const newRegisterSalePaymentId = generateTransactionId()

        trackUserEvent(
          WEB_REGISTER.DISPLAY_NAME,
          'LSD user switched to terminal tab',
          {
            oldRegisterSalePaymentId,
            newRegisterSalePaymentId,
            registerSaleId,
          }
        )
      } else {
        //   If the payment was initiated and switched to CNP (Card Not Present) tab,
        //   we don't need to recover the transaction; the transaction will be canceled when switching tabs.
        //   Additionally, if the user switches back to the terminal tab,
        //   we generate a new transactionId as we no longer need to keep the old one.
        trackUserEvent(
          WEB_REGISTER.DISPLAY_NAME,
          'LSD user switched to cnp tab',
          {
            registerSalePaymentId: transactionId,
            registerSaleId,
          }
        )
        removeFromStorage(ACTIVE_LSPAY_TRANSACTION)

        // Cancel any pending terminal transaction immediately using the ref
        // This ensures cancellation happens even if the createTransaction API
        // hasn't completed yet (the ref will be set when it does complete)
        invokeCancel()
      }
    }

    setPreviousSelectedTab(selectedTab)
  }

  // Hoisted to avoid duplicate t() calls across the two branches below.
  const terminalTabLabel = t('Tap, insert, or swipe card', {
    _key: 'Payments.PayScreen.LSPAYPaymentModal.TerminalTab.label',
    _context:
      'Tab label in the payment modal for transactions using a physical card',
  })
  const manualCardEntryTabLabel = t('Enter card details manually', {
    _key: 'Payments.PayScreen.LSPAYPaymentModal.ManualCardEntryTab.label',
    _context:
      'Tab label in the payment modal for entering card details manually',
  })

  // Shared by both tab-content variants below; each wraps it differently.
  const renderTerminalTabContent = () =>
    showTerminalStatusWarning ? (
      <TerminalStatusContent setDismissible={setDismissible} />
    ) : (
      <PaymentComponent
        onSuccess={closeModal}
        onError={onError}
        amount={amount}
        outletId={outletId}
        register={register}
        registerSaleId={registerSaleId}
        saleTotal={saleTotal}
        retailerPaymentType={retailerPaymentType}
        transactionId={transactionId}
        currency={currency}
        setDismissible={setDismissible}
        setDisableCNP={setDisableCNP}
        setIntentClientSecret={setIntentClientSecret}
        setSwiperError={setSwiperError}
        paymentIntentId={paymentIntentId}
        isRecovery={isRecovery}
        withSurcharge={withSurcharge}
        {...(tipEligibleAmount?.toNumber() !== 0 ? { tipEligibleAmount } : {})}
        pairing={pairing}
        setCancelProcessing={setCancelProcessing}
        generateTransactionId={generateTransactionId}
        shouldUseSimulator={shouldUseSimulator}
      />
    )

  // Used by the split-entry variant once shouldShowSignpost is known.
  const renderSignpostTabBar = () =>
    shouldShowSignpost && (
      <Tabs className="vd-ml8 vd-mr8">
        <Tab
          data-track={PAYMENT_MODAL_TAB_DATA_TRACK.TERMINAL}
          name={PAYMENT_MODAL_TAB.TERMINAL}
          disabled={disableTerminalPayment}
        >
          {terminalTabLabel}
        </Tab>
        <Tab
          data-track={PAYMENT_MODAL_TAB_DATA_TRACK.MANUAL_CARD_ENTRY}
          name={PAYMENT_MODAL_TAB.SIGNPOST}
        >
          {manualCardEntryTabLabel}
        </Tab>
      </Tabs>
    )

  // lspay_cp_cnp_split_entry == true -> Render new Modal UI.
  // Terminal stays mounted at a stable position (aria-hidden/display:none)
  // so PaymentComponent never remounts when shouldShowSignpost resolves.
  const renderSplitEntryTabContent = () => (
    <>
      {/* Only render the tab bar (and signpost tab) once eligibility is known */}
      {renderSignpostTabBar()}
      <div
        aria-hidden={signpostTabSelected ? true : undefined}
        style={signpostTabSelected ? { display: 'none' } : undefined}
      >
        {renderTerminalTabContent()}
      </div>
      {signpostTabSelected && (
        <LSPAYCnpOnboardingSignpost onDismiss={handleSignpostDismiss} />
      )}
    </>
  )

  // lspay_cp_cnp_split_entry == false → Render Standard UI.
  // Display tabbed UI with functional CNP Tab content.
  const renderStandardTabContent = () => (
    <>
      <Tabs className="vd-ml8 vd-mr8">
        <Tab
          data-track={PAYMENT_MODAL_TAB_DATA_TRACK.TERMINAL}
          name={PAYMENT_MODAL_TAB.TERMINAL}
          disabled={disableTerminalPayment}
        >
          {terminalTabLabel}
        </Tab>
        {/* previousSelectedTab holds the current tab outside handleOnChange */}
        <SsboTourStep
          stepId="lspayTerminal"
          enabled={
            previousSelectedTab === PAYMENT_MODAL_TAB.TERMINAL && !disableCNP
          }
        >
          <Tab
            data-track={PAYMENT_MODAL_TAB_DATA_TRACK.MANUAL_CARD_ENTRY}
            name={PAYMENT_MODAL_TAB.CNP}
            disabled={disableCNP}
          >
            {manualCardEntryTabLabel}
          </Tab>
        </SsboTourStep>
      </Tabs>
      <TabContent name={PAYMENT_MODAL_TAB.TERMINAL}>
        {renderTerminalTabContent()}
      </TabContent>
      <TabContent name={PAYMENT_MODAL_TAB.CNP}>
        {useProcessingPMOF ? (
          <LSPAYCnpPmofPayment
            amount={amount}
            customerId={customerId}
            outletId={outletId}
            register={register}
            registerSaleId={registerSaleId}
            registerSalePaymentId={transactionId}
            retailerPaymentTypeId={retailerPaymentType.id}
            setSwiperError={setSwiperError}
            setDisableTerminalPayment={setDisableTerminalPayment}
            onError={onError}
            onSuccess={closeModal}
            onClose={cancelAndCloseModal}
            cancelProcessing={guardedCancelProcessing}
          />
        ) : (
          <LSPAYCardNotPresentPayment
            setDisableTerminalPayment={setDisableTerminalPayment}
            close={closeModal}
            onCancelPayment={cancelAndCloseModal}
            outletId={outletId}
            amount={amount}
            currency={currency}
            register={register}
            retailerPaymentTypeId={retailerPaymentType.id}
            registerSaleId={registerSaleId}
            transactionId={initialTransactionId}
            intentClientSecret={intentClientSecret}
            setSwiperError={setSwiperError}
            hasMoto={lspayCNPMotoFlag}
            cancelProcessing={guardedCancelProcessing}
          />
        )}
      </TabContent>
    </>
  )

  // TODO: LSPAY-43773 — once lspay_cp_cnp_split_entry is fully rolled out and
  // stable in Prod, delete this check and renderStandardTabContent, and
  // always use renderSplitEntryTabContent.
  const renderTabContent = lspayCpCnpSplitEntryFlag
    ? renderSplitEntryTabContent
    : renderStandardTabContent

  return (
    <Modal
      size="medium"
      focusTrapActive={true}
      dismissable={dismissible}
      onClose={onError}
      className="lspay-payment-modal"
    >
      {showHeader && (
        <ModalHeader>
          {t('Pay {amount} with {retailerPaymentTypeName}', {
            amount: vdCurrencyFilter(amount.toNumber()),
            retailerPaymentTypeName: retailerPaymentType.name,
            _context: 'Payment modal header',
          })}
        </ModalHeader>
      )}
      {isRefund ? (
        <>
          {/* Choose between advanced refund component (RefundComponent) vs simple referenced-only component (LSPAYReferencedRefundOnly).

              RefundComponent is chosen and will be:
              - LSPAYRefundServerDriven (server-driven API endpoint) if shouldServerDrivenRefund is true, OR
              - LSPAYRefundClientDriven (legacy API endpoint) otherwise

              For Processing Service payments, shouldServerDrivenRefund is false,
              so RefundComponent = LSPAYRefundClientDriven.

              Use RefundComponent if any of these conditions are true:
              - allowUnrefRefund: User is allowed to do unreferenced refunds
              - shouldServerDrivenRefund: Server-driven API is available and should be used
              - originalSaleHasProcessingServicePayment: Payment was via Processing Service (requires legacy API endpoint)
              - No payment IDs or multiple payment IDs: Requires unreferenced refund handling

              Otherwise use LSPAYReferencedRefundOnly for simple single-payment referenced refunds. */}
          {allowUnrefRefund ||
          shouldServerDrivenRefund ||
          originalSaleHasProcessingServicePayment ||
          !originalRegisterSalePaymentIds ||
          originalRegisterSalePaymentIds.length !== 1 ? (
            <RefundComponent
              currency={currency}
              onSuccess={closeModal}
              onError={onError}
              amount={amount}
              disableUnrefRefund={!allowUnrefRefund}
              registerSaleId={registerSaleId}
              originalRegisterSalePaymentIds={originalRegisterSalePaymentIds}
              originalSalePaidByOtherPT={originalSalePaidByOtherPT}
              originalSaleHasProcessingServicePayment={
                originalSaleHasProcessingServicePayment
              }
              originalLspayPaymentAmount={originalLspayPaymentAmount}
              register={lspayRegister}
              outletId={outletId}
              transactionId={transactionId}
              retailerPaymentType={retailerPaymentType}
              setDismissible={setDismissible}
              generateTransactionId={generateTransactionId}
              pairing={pairing}
            />
          ) : (
            <LSPAYReferencedRefundOnly
              currency={currency}
              onSuccess={closeModal}
              onError={onError}
              amount={amount}
              originalRegisterSalePaymentId={originalRegisterSalePaymentIds![0]}
              register={lspayRegister}
              outletId={outletId}
              transactionId={initialTransactionId}
              retailerPaymentType={retailerPaymentType}
            />
          )}
        </>
      ) : lspayCNPPaymentsFlag && !forceFlow ? (
        // Single shared SelectedTabProvider — its props never change since
        // lspayCpCnpSplitEntryFlag is stable for the life of the mount.
        <SelectedTabProvider
          defaultTab={PAYMENT_MODAL_TAB.TERMINAL}
          onChange={selectedTab => handleOnChange(selectedTab)}
        >
          {renderTabContent()}
        </SelectedTabProvider>
      ) : forceFlow === 'pmof' && paymentMethodOnFileId && customerId ? (
        <LSPAYPaymentMethodOnFile
          amount={amount}
          customerId={customerId}
          outletId={outletId}
          paymentMethodOnFileId={paymentMethodOnFileId}
          register={register}
          registerSaleId={registerSaleId}
          transactionId={transactionId}
          close={closeModal}
          setDismissible={setDismissible}
          setSwiperError={setSwiperError}
        />
      ) : (
        <PaymentComponent
          onSuccess={closeModal}
          onError={onError}
          amount={amount}
          outletId={outletId}
          register={register}
          registerSaleId={registerSaleId}
          saleTotal={saleTotal}
          retailerPaymentType={retailerPaymentType}
          transactionId={transactionId}
          currency={currency}
          setDismissible={setDismissible}
          setDisableCNP={setDisableCNP}
          setIntentClientSecret={setIntentClientSecret}
          setSwiperError={setSwiperError}
          paymentIntentId={paymentIntentId}
          isRecovery={isRecovery}
          withSurcharge={withSurcharge}
          {...(tipEligibleAmount?.toNumber() !== 0
            ? { tipEligibleAmount }
            : {})}
          pairing={pairing}
          setCancelProcessing={setCancelProcessing}
          generateTransactionId={generateTransactionId}
        />
      )}
    </Modal>
  )
}
